'use client';
import React, { useState, useMemo, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Consultant {
  id: string;
  name: string;
  initials: string;
  title: string;
  expertise: string[];
  category: string;
  bio: string;
  rating: number;
  reviewCount: number;
  hourlyRate: number;
  availability: 'available' | 'limited' | 'busy';
  location: string;
  languages: string[];
  verified: boolean;
  responseTime: string;
  completedSessions: number;
  badges: string[];
  featured?: boolean;
}

type SortOption = 'rating' | 'rate_asc' | 'rate_desc' | 'reviews' | 'sessions';
type AvailabilityFilter = 'all' | 'available' | 'limited' | 'busy';
type ViewMode = 'grid' | 'map';

const categories = [
  { id: 'all', label: 'All Expertise', icon: 'Squares2X2Icon' },
  { id: 'finance', label: 'Islamic Finance', icon: 'BanknotesIcon' },
  { id: 'business', label: 'Business Strategy', icon: 'BriefcaseIcon' },
  { id: 'legal', label: 'Halal Legal', icon: 'ScaleIcon' },
  { id: 'marketing', label: 'Marketing', icon: 'MegaphoneIcon' },
  { id: 'tech', label: 'Tech & Digital', icon: 'ComputerDesktopIcon' },
];

const availabilityConfig = {
  available: { label: 'Available', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  limited: { label: 'Limited', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  busy: { label: 'Busy', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

// Region groupings for map view
const regionGroups = [
  { id: 'middle-east', label: 'Middle East & Gulf', color: 'bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800', dot: 'bg-amber-500', keywords: ['Dubai', 'UAE', 'Saudi', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Jordan', 'Lebanon', 'Egypt'] },
  { id: 'south-asia', label: 'South Asia', color: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800', dot: 'bg-emerald-500', keywords: ['Pakistan', 'Bangladesh', 'India', 'Sri Lanka', 'Nepal'] },
  { id: 'southeast-asia', label: 'Southeast Asia', color: 'bg-teal-50 border-teal-200 dark:bg-teal-900/10 dark:border-teal-800', dot: 'bg-teal-500', keywords: ['Malaysia', 'Indonesia', 'Singapore', 'Philippines', 'Thailand'] },
  { id: 'africa', label: 'Africa', color: 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800', dot: 'bg-orange-500', keywords: ['Nigeria', 'Kenya', 'Somalia', 'Ethiopia', 'Ghana', 'Tanzania', 'Uganda', 'Morocco', 'Tunisia'] },
  { id: 'europe-na', label: 'Europe & North America', color: 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800', dot: 'bg-blue-500', keywords: ['UK', 'USA', 'Canada', 'Germany', 'France', 'Netherlands', 'Sweden', 'London', 'New York', 'Toronto'] },
  { id: 'other', label: 'Other Regions', color: 'bg-purple-50 border-purple-200 dark:bg-purple-900/10 dark:border-purple-800', dot: 'bg-purple-500', keywords: [] },
];

function getRegion(location: string) {
  const loc = location.toLowerCase();
  for (const region of regionGroups) {
    if (region.keywords.some((k) => loc.includes(k.toLowerCase()))) return region.id;
  }
  return 'other';
}

// ─── Star Rating Component ─────────────────────────────────────────────────────
function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          width={size}
          height={size}
          viewBox="0 0 20 20"
          fill={star <= Math.round(rating) ? '#f59e0b' : 'none'}
          stroke={star <= Math.round(rating) ? '#f59e0b' : '#d1d5db'}
          strokeWidth="1.5"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

// ─── Instant Book Modal ────────────────────────────────────────────────────────
interface InstantBookModalProps {
  consultant: Consultant;
  onClose: () => void;
}

function InstantBookModal({ consultant, onClose }: InstantBookModalProps) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [sessionType, setSessionType] = useState<'30min' | '60min' | '90min'>('60min');
  const [booked, setBooked] = useState(false);

  const sessionOptions = [
    { value: '30min', label: '30 min', price: Math.round(consultant.hourlyRate * 0.5) },
    { value: '60min', label: '60 min', price: consultant.hourlyRate },
    { value: '90min', label: '90 min', price: Math.round(consultant.hourlyRate * 1.5) },
  ] as const;

  const timeSlots = ['09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00', '17:00'];

  const selectedOption = sessionOptions.find((o) => o.value === sessionType)!;

  const handleBook = () => {
    if (!selectedDate || !selectedTime) return;
    setBooked(true);
  };

  if (booked) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-card border border-border rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircleIcon" size={36} className="text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-700 text-foreground mb-1">Session Requested!</h3>
          <p className="text-sm text-muted-foreground mb-1">
            Your booking request has been sent to <span className="font-600 text-foreground">{consultant.name}</span>.
          </p>
          <p className="text-xs text-muted-foreground mb-6">
            {selectedDate} at {selectedTime} · {selectedOption.label} · ${selectedOption.price}
          </p>
          <div className="flex gap-3">
            <Link
              href={`/book-session?consultant=${consultant.id}`}
              className="flex-1 text-center text-sm font-600 bg-primary text-primary-foreground py-2.5 rounded-xl hover:bg-primary/90 transition-colors"
            >
              View Booking
            </Link>
            <button
              onClick={onClose}
              className="flex-1 text-sm font-600 border border-border text-foreground py-2.5 rounded-xl hover:bg-muted transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-teal flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {consultant.initials}
            </div>
            <div>
              <h3 className="text-sm font-700 text-foreground">{consultant.name}</h3>
              <p className="text-xs text-muted-foreground">{consultant.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Session Duration */}
          <div>
            <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2 block">Session Duration</label>
            <div className="grid grid-cols-3 gap-2">
              {sessionOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSessionType(opt.value)}
                  className={`flex flex-col items-center py-3 rounded-xl border text-xs font-600 transition-all ${
                    sessionType === opt.value
                      ? 'bg-primary/10 border-primary text-primary' :'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <span className="text-sm font-700">{opt.label}</span>
                  <span className="text-xs font-500 mt-0.5">${opt.price}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date Picker */}
          <div>
            <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2 block">Select Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {/* Time Slots */}
          <div>
            <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2 block">Available Times</label>
            <div className="grid grid-cols-4 gap-2">
              {timeSlots.map((slot) => (
                <button
                  key={slot}
                  onClick={() => setSelectedTime(slot)}
                  className={`py-2 rounded-xl text-xs font-600 border transition-all ${
                    selectedTime === slot
                      ? 'bg-primary/10 border-primary text-primary' :'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          {selectedDate && selectedTime && (
            <div className="bg-muted/50 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-base font-700 text-foreground">${selectedOption.price}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{selectedDate}</p>
                <p className="text-xs font-600 text-foreground">{selectedTime} · {selectedOption.label}</p>
              </div>
            </div>
          )}

          {/* Book Button */}
          <button
            onClick={handleBook}
            disabled={!selectedDate || !selectedTime}
            className="w-full py-3 rounded-xl text-sm font-700 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Confirm Booking · ${selectedOption.price}
          </button>
          <p className="text-xs text-center text-muted-foreground -mt-2">
            You won&apos;t be charged until the consultant confirms
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Featured Consultant Card ──────────────────────────────────────────────────
function FeaturedCard({ consultant, onInstantBook }: { consultant: Consultant; onInstantBook: (c: Consultant) => void }) {
  const avail = availabilityConfig[consultant.availability] ?? availabilityConfig.available;
  return (
    <div className="relative bg-gradient-to-br from-primary/5 via-card to-card border-2 border-primary/20 rounded-2xl p-5 flex flex-col gap-3 hover:shadow-xl hover:border-primary/40 transition-all duration-200 group overflow-hidden">
      {/* Featured badge */}
      <div className="absolute top-3 right-3 flex items-center gap-1 bg-primary text-primary-foreground text-xs font-700 px-2.5 py-1 rounded-full shadow-sm">
        <Icon name="StarIcon" size={11} />
        Featured
      </div>
      {/* Decorative glow */}
      <div className="absolute -top-8 -left-8 w-32 h-32 bg-primary/5 rounded-full blur-2xl pointer-events-none" />

      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-2xl gradient-teal flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-md">
          {consultant.initials}
        </div>
        <div className="flex-1 min-w-0 pr-16">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="text-sm font-700 text-foreground">{consultant.name}</h3>
            {consultant.verified && (
              <Icon name="CheckBadgeIcon" size={15} className="text-primary flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{consultant.title}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${avail.color}`}>{avail.label}</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Icon name="MapPinIcon" size={11} />
              {consultant.location}
            </span>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{consultant.bio}</p>

      <div className="flex flex-wrap gap-1.5">
        {consultant.expertise.slice(0, 3).map((skill) => (
          <span key={skill} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-lg font-500">{skill}</span>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-border">
        <div className="flex items-center gap-1.5">
          <StarRating rating={consultant.rating} size={13} />
          <span className="text-xs font-700 text-foreground">{consultant.rating.toFixed(1)}</span>
          <span className="text-xs text-muted-foreground">({consultant.reviewCount})</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Icon name="CheckCircleIcon" size={12} />
          {consultant.completedSessions} sessions
        </div>
        <div className="ml-auto text-right">
          <span className="text-base font-700 text-foreground">${consultant.hourlyRate}</span>
          <span className="text-xs text-muted-foreground">/hr</span>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onInstantBook(consultant)}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-700 bg-primary text-primary-foreground py-2.5 rounded-xl hover:bg-primary/90 transition-colors"
        >
          <Icon name="BoltIcon" size={13} />
          Instant Book
        </button>
        <Link
          href="/consultation-messages"
          className="flex-1 text-center text-xs font-600 border border-border text-foreground py-2.5 rounded-xl hover:bg-muted transition-colors"
        >
          Message
        </Link>
      </div>
    </div>
  );
}

// ─── Regular Consultant Card ───────────────────────────────────────────────────
function ConsultantCard({ consultant, onInstantBook }: { consultant: Consultant; onInstantBook: (c: Consultant) => void }) {
  const avail = availabilityConfig[consultant.availability] ?? availabilityConfig.available;
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-4 hover:shadow-lg hover:border-primary/30 transition-all duration-200 group">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl gradient-teal flex items-center justify-center text-white font-bold text-base flex-shrink-0">
          {consultant.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-600 text-foreground truncate">{consultant.name}</h3>
            {consultant.verified && (
              <span className="flex items-center gap-0.5 text-xs text-primary font-500">
                <Icon name="CheckBadgeIcon" size={14} className="text-primary" />
                Verified
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{consultant.title}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${avail.color}`}>
              {avail.label}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Icon name="MapPinIcon" size={11} />
              {consultant.location}
            </span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-base font-700 text-foreground">${consultant.hourlyRate}</p>
          <p className="text-xs text-muted-foreground">/hour</p>
        </div>
      </div>

      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{consultant.bio}</p>

      <div className="flex flex-wrap gap-1.5">
        {consultant.expertise.slice(0, 3).map((skill) => (
          <span key={skill} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-lg font-500">
            {skill}
          </span>
        ))}
        {consultant.expertise.length > 3 && (
          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-lg font-500">
            +{consultant.expertise.length - 3} more
          </span>
        )}
      </div>

      <div className="flex items-center gap-4 pt-1 border-t border-border">
        <div className="flex items-center gap-1.5">
          <StarRating rating={consultant.rating} />
          <span className="text-xs font-600 text-foreground">{consultant.rating.toFixed(1)}</span>
          <span className="text-xs text-muted-foreground">({consultant.reviewCount})</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Icon name="CheckCircleIcon" size={13} />
          <span>{consultant.completedSessions} sessions</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
          <Icon name="ClockIcon" size={13} />
          <span>{consultant.responseTime}</span>
        </div>
      </div>

      {consultant.badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {consultant.badges.map((badge) => (
            <span key={badge} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-500">
              {badge}
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-2 mt-auto">
        <button
          onClick={() => onInstantBook(consultant)}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-600 bg-primary text-primary-foreground py-2 rounded-xl hover:bg-primary/90 transition-colors duration-150"
        >
          <Icon name="BoltIcon" size={12} />
          Instant Book
        </button>
        <Link
          href={`/book-session?consultant=${consultant.id}`}
          className="px-3 text-center text-xs font-600 border border-border text-foreground py-2 rounded-xl hover:bg-muted transition-colors duration-150"
        >
          Full Booking
        </Link>
        <Link
          href="/consultation-messages"
          className="px-3 text-center text-xs font-600 border border-border text-foreground py-2 rounded-xl hover:bg-muted transition-colors duration-150"
        >
          Message
        </Link>
      </div>
    </div>
  );
}

// ─── Map View ─────────────────────────────────────────────────────────────────
function MapView({ consultants, onInstantBook }: { consultants: Consultant[]; onInstantBook: (c: Consultant) => void }) {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  const regionData = useMemo(() => {
    return regionGroups.map((region) => {
      const members = consultants.filter((c) => getRegion(c.location) === region.id);
      return { ...region, members };
    }).filter((r) => r.members.length > 0);
  }, [consultants]);

  const displayConsultants = selectedRegion
    ? consultants.filter((c) => getRegion(c.location) === selectedRegion)
    : consultants;

  return (
    <div className="space-y-5">
      {/* Region selector */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="GlobeAltIcon" size={18} className="text-primary" />
          <h3 className="text-sm font-700 text-foreground">Browse by Region</h3>
          <span className="text-xs text-muted-foreground ml-auto">{consultants.length} consultants worldwide</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {regionData.map((region) => (
            <button
              key={region.id}
              onClick={() => setSelectedRegion(selectedRegion === region.id ? null : region.id)}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-150 ${
                selectedRegion === region.id
                  ? `${region.color} border-current`
                  : 'border-border hover:border-primary/30 hover:bg-muted'
              }`}
            >
              <div className={`w-3 h-3 rounded-full flex-shrink-0 ${region.dot}`} />
              <div className="min-w-0">
                <p className="text-xs font-600 text-foreground truncate">{region.label}</p>
                <p className="text-xs text-muted-foreground">{region.members.length} consultant{region.members.length !== 1 ? 's' : ''}</p>
              </div>
            </button>
          ))}
        </div>
        {selectedRegion && (
          <button
            onClick={() => setSelectedRegion(null)}
            className="mt-3 text-xs text-primary hover:underline font-500 flex items-center gap-1"
          >
            <Icon name="XMarkIcon" size={13} />
            Clear region filter
          </button>
        )}
      </div>

      {/* Consultants in selected region */}
      <div>
        {selectedRegion && (
          <p className="text-sm text-muted-foreground mb-3">
            Showing <span className="font-600 text-foreground">{displayConsultants.length}</span> consultant{displayConsultants.length !== 1 ? 's' : ''} in{' '}
            <span className="text-primary font-500">{regionGroups.find((r) => r.id === selectedRegion)?.label}</span>
          </p>
        )}
        {displayConsultants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Icon name="MapPinIcon" size={32} className="text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No consultants in this region yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {displayConsultants.map((c) => (
              <div key={c.id} className="relative">
                {/* Region dot indicator */}
                <div className={`absolute -top-1.5 -left-1.5 w-4 h-4 rounded-full border-2 border-card z-10 ${regionGroups.find((r) => r.id === getRegion(c.location))?.dot ?? 'bg-muted'}`} />
                <ConsultantCard consultant={c} onInstantBook={onInstantBook} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function BrowseConsultantsPage() {
  const supabase = createClient();
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>('all');
  const [minRate, setMinRate] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [minRating, setMinRating] = useState('0');
  const [sortBy, setSortBy] = useState<SortOption>('rating');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [instantBookConsultant, setInstantBookConsultant] = useState<Consultant | null>(null);

  useEffect(() => {
    async function fetchConsultants() {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('consultant_profiles')
          .select(`
            id,
            display_name,
            professional_title,
            bio,
            location,
            languages,
            category,
            expertise_areas,
            skills,
            hourly_rate_usd,
            availability_status,
            is_verified,
            years_experience,
            certifications,
            user_id
          `)
          .eq('is_active', true)
          .eq('onboarding_completed', true);

        if (fetchError) {
          setError(fetchError.message);
          setLoading(false);
          return;
        }

        const { data: reviewStats } = await supabase
          .from('session_reviews')
          .select('reviewee_id, rating');

        const { data: bookingStats } = await supabase
          .from('consultation_bookings')
          .select('provider_user_id, status')
          .eq('status', 'completed');

        const reviewMap: Record<string, { total: number; count: number }> = {};
        reviewStats?.forEach((r) => {
          if (!reviewMap[r.reviewee_id]) reviewMap[r.reviewee_id] = { total: 0, count: 0 };
          reviewMap[r.reviewee_id].total += r.rating;
          reviewMap[r.reviewee_id].count += 1;
        });

        const sessionMap: Record<string, number> = {};
        bookingStats?.forEach((b) => {
          if (b.provider_user_id) {
            sessionMap[b.provider_user_id] = (sessionMap[b.provider_user_id] ?? 0) + 1;
          }
        });

        const mapped: Consultant[] = (data ?? []).map((row, idx) => {
          const nameWords = (row.display_name ?? '').split(' ');
          const initials = nameWords.slice(0, 2).map((w: string) => w[0] ?? '').join('').toUpperCase();
          const stats = reviewMap[row.user_id] ?? { total: 0, count: 0 };
          const avgRating = stats.count > 0 ? stats.total / stats.count : 0;
          const badges: string[] = [];
          if (row.is_verified) badges.push('Verified Expert');
          if ((row.certifications ?? []).length > 0) badges.push('Certified');
          const availability = (['available', 'limited', 'busy'].includes(row.availability_status)
            ? row.availability_status
            : 'available') as 'available' | 'limited' | 'busy';

          return {
            id: row.id,
            name: row.display_name ?? '',
            initials,
            title: row.professional_title ?? '',
            expertise: [...(row.expertise_areas ?? []), ...(row.skills ?? [])].slice(0, 8),
            category: row.category ?? 'general',
            bio: row.bio ?? '',
            rating: parseFloat(avgRating.toFixed(2)),
            reviewCount: stats.count,
            hourlyRate: parseFloat(row.hourly_rate_usd ?? 0),
            availability,
            location: row.location ?? '',
            languages: row.languages ?? [],
            verified: row.is_verified ?? false,
            responseTime: '< 2 hours',
            completedSessions: sessionMap[row.user_id] ?? 0,
            badges,
            featured: idx < 3, // top 3 from DB (already sorted by rating server-side ideally)
          };
        });

        setConsultants(mapped);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load consultants');
      } finally {
        setLoading(false);
      }
    }

    fetchConsultants();
  }, []);

  const filtered = useMemo(() => {
    let list = [...consultants];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.title.toLowerCase().includes(q) ||
          c.expertise.some((e) => e.toLowerCase().includes(q)) ||
          c.bio.toLowerCase().includes(q)
      );
    }

    if (selectedCategory !== 'all') {
      list = list.filter((c) => c.category === selectedCategory);
    }

    if (availabilityFilter !== 'all') {
      list = list.filter((c) => c.availability === availabilityFilter);
    }

    if (minRate) list = list.filter((c) => c.hourlyRate >= Number(minRate));
    if (maxRate) list = list.filter((c) => c.hourlyRate <= Number(maxRate));
    if (minRating !== '0') list = list.filter((c) => c.rating >= Number(minRating));

    switch (sortBy) {
      case 'rating': list.sort((a, b) => b.rating - a.rating); break;
      case 'rate_asc': list.sort((a, b) => a.hourlyRate - b.hourlyRate); break;
      case 'rate_desc': list.sort((a, b) => b.hourlyRate - a.hourlyRate); break;
      case 'reviews': list.sort((a, b) => b.reviewCount - a.reviewCount); break;
      case 'sessions': list.sort((a, b) => b.completedSessions - a.completedSessions); break;
    }

    return list;
  }, [consultants, searchQuery, selectedCategory, availabilityFilter, minRate, maxRate, minRating, sortBy]);

  // Featured = top 3 by rating from the full (unfiltered) list
  const featuredConsultants = useMemo(() => {
    return [...consultants]
      .filter((c) => c.availability !== 'busy')
      .sort((a, b) => b.rating - a.rating || b.completedSessions - a.completedSessions)
      .slice(0, 3);
  }, [consultants]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setAvailabilityFilter('all');
    setMinRate('');
    setMaxRate('');
    setMinRating('0');
    setSortBy('rating');
  };

  const hasActiveFilters =
    searchQuery || selectedCategory !== 'all' || availabilityFilter !== 'all' || minRate || maxRate || minRating !== '0';

  const showFeatured = !hasActiveFilters && viewMode === 'grid' && featuredConsultants.length > 0 && !loading;

  return (
    <AppLayout activeRoute="/browse-consultants">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-700 text-foreground">Browse Consultants</h1>
          <p className="text-sm text-muted-foreground">
            Find verified experts in Islamic finance, business strategy, legal compliance, and more.
          </p>
        </div>

        {/* Search Bar + Filter Toggle + View Toggle */}
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Icon
              name="MagnifyingGlassIcon"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by name, expertise, or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="XMarkIcon" size={16} />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-500 transition-all duration-150 ${
              showFilters || hasActiveFilters
                ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name="AdjustmentsHorizontalIcon" size={16} />
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-primary" />
            )}
          </button>
          {/* View Mode Toggle */}
          <div className="flex items-center bg-card border border-border rounded-xl overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-500 transition-colors ${
                viewMode === 'grid' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              title="Grid view"
            >
              <Icon name="Squares2X2Icon" size={16} />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-500 transition-colors ${
                viewMode === 'map' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              title="Map view"
            >
              <Icon name="GlobeAltIcon" size={16} />
            </button>
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-3 py-2.5 bg-card border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            <option value="rating">Top Rated</option>
            <option value="rate_asc">Rate: Low to High</option>
            <option value="rate_desc">Rate: High to Low</option>
            <option value="reviews">Most Reviews</option>
            <option value="sessions">Most Sessions</option>
          </select>
        </div>

        {/* Expandable Filters Panel */}
        {showFilters && (
          <div className="bg-card border border-border rounded-2xl p-5 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-600 text-foreground">Filter Consultants</h3>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-primary hover:underline font-500">
                  Clear all filters
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Availability */}
              <div className="space-y-2">
                <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Availability</label>
                <div className="flex flex-col gap-1.5">
                  {(['all', 'available', 'limited', 'busy'] as AvailabilityFilter[]).map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setAvailabilityFilter(opt)}
                      className={`text-left text-xs px-3 py-1.5 rounded-lg font-500 transition-colors ${
                        availabilityFilter === opt
                          ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {opt === 'all' ? 'Any Availability' : availabilityConfig[opt].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Min Rating */}
              <div className="space-y-2">
                <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Minimum Rating</label>
                <div className="flex flex-col gap-1.5">
                  {['0', '4', '4.5', '4.8'].map((val) => (
                    <button
                      key={val}
                      onClick={() => setMinRating(val)}
                      className={`text-left text-xs px-3 py-1.5 rounded-lg font-500 transition-colors ${
                        minRating === val
                          ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      {val === '0' ? 'Any Rating' : `${val}+ stars`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hourly Rate Range */}
              <div className="space-y-2">
                <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Hourly Rate (USD)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={minRate}
                    onChange={(e) => setMinRate(e.target.value)}
                    className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <span className="text-muted-foreground text-xs">–</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxRate}
                    onChange={(e) => setMaxRate(e.target.value)}
                    className="w-full px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {[['0', '100'], ['100', '150'], ['150', '200'], ['200', '']].map(([min, max]) => (
                    <button
                      key={`${min}-${max}`}
                      onClick={() => { setMinRate(min); setMaxRate(max); }}
                      className="text-xs px-2 py-0.5 rounded-lg bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors font-500"
                    >
                      {max ? `$${min}–$${max}` : `$${min}+`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="space-y-2">
                <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Quick Stats</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Total consultants</span>
                    <span className="font-600 text-foreground">{consultants.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Available now</span>
                    <span className="font-600 text-green-600">{consultants.filter(c => c.availability === 'available').length}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Verified experts</span>
                    <span className="font-600 text-primary">{consultants.filter(c => c.verified).length}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Showing results</span>
                    <span className="font-600 text-foreground">{filtered.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category Pills */}
        <div className="flex gap-2 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-500 border transition-all duration-150 ${
                selectedCategory === cat.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={cat.icon as Parameters<typeof Icon>[0]['name']} size={14} />
              {cat.label}
            </button>
          ))}
        </div>

        {/* ── Featured Consultants Section ── */}
        {showFeatured && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <Icon name="StarIcon" size={16} className="text-amber-500" />
                <h2 className="text-sm font-700 text-foreground">Featured Consultants</h2>
              </div>
              <span className="text-xs text-muted-foreground">Top-rated experts available now</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featuredConsultants.map((c) => (
                <FeaturedCard key={`featured-${c.id}`} consultant={c} onInstantBook={setInstantBookConsultant} />
              ))}
            </div>
            <div className="border-t border-border pt-2" />
          </div>
        )}

        {/* Results Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {loading ? (
              <span>Loading consultants…</span>
            ) : (
              <>
                <span className="font-600 text-foreground">{filtered.length}</span>{' '}
                consultant{filtered.length !== 1 ? 's' : ''} found
                {selectedCategory !== 'all' && (
                  <span> in <span className="text-primary font-500">{categories.find(c => c.id === selectedCategory)?.label}</span></span>
                )}
              </>
            )}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              <Icon name="XMarkIcon" size={14} />
              Clear filters
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-5 animate-pulse">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-muted rounded" />
                  <div className="h-3 bg-muted rounded w-4/5" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
              <Icon name="ExclamationCircleIcon" size={28} className="text-red-500" />
            </div>
            <h3 className="text-base font-600 text-foreground mb-1">Failed to load consultants</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs">{error}</p>
          </div>
        )}

        {/* ── Map View ── */}
        {!loading && !error && viewMode === 'map' && (
          <MapView consultants={filtered} onInstantBook={setInstantBookConsultant} />
        )}

        {/* ── Grid View ── */}
        {!loading && !error && viewMode === 'grid' && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((consultant) => (
              <ConsultantCard key={consultant.id} consultant={consultant} onInstantBook={setInstantBookConsultant} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && viewMode === 'grid' && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Icon name="MagnifyingGlassIcon" size={28} className="text-muted-foreground" />
            </div>
            <h3 className="text-base font-600 text-foreground mb-1">
              {consultants.length === 0 ? 'No consultants yet' : 'No consultants found'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs">
              {consultants.length === 0
                ? 'Consultants will appear here once they complete onboarding.'
                : 'Try adjusting your search terms or filters to find the right expert.'}
            </p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm font-500 text-primary hover:underline">
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Instant Book Modal */}
      {instantBookConsultant && (
        <InstantBookModal
          consultant={instantBookConsultant}
          onClose={() => setInstantBookConsultant(null)}
        />
      )}
    </AppLayout>
  );
}
